'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { Select as SelectPrimitive } from 'radix-ui';
import React, { type ComponentProps } from 'react';
import { ChevronDown, Close, Check } from '../../../icons';
import { cn } from '../../../lib/utils';
import { Icon, type SvgIcon } from '../../icon/icon';
import {
  type SelectContextValue,
  SelectProvider,
  useSelectContextOptional,
} from './select-context';
import { useFieldContext } from '../../field/field';

export const Select = ({
  value,
  onValueChange,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) => {
  // Context is only provided in controlled mode (value + onValueChange both present).
  // Uncontrolled usage gets no provider, so SelectTrigger's useSelectContextOptional()
  // returns undefined and skips the clear-button logic entirely.
  const contextValue = React.useMemo<SelectContextValue | undefined>(
    () =>
      value !== undefined && onValueChange
        ? { value, clearValue: () => onValueChange('') }
        : undefined,
    [value, onValueChange],
  );

  return (
    <SelectPrimitive.Root data-slot="select" value={value} onValueChange={onValueChange} {...props}>
      {contextValue ? <SelectProvider value={contextValue}>{children}</SelectProvider> : children}
    </SelectPrimitive.Root>
  );
};

export const SelectGroup = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) => (
  <SelectPrimitive.Group data-slot="select-group" className={cn(className)} {...props} />
);

export const SelectValue = ({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) => (
  <SelectPrimitive.Value data-slot="select-value" {...props} />
);

const selectTriggerVariants = cva(
  cn(
    // layout + sizing
    'relative flex h-[44px] w-full items-center justify-between gap-2 px-4 select-none',
    // truncate text so it never overlaps the chevron / clear button
    '[&>span]:truncate',
    // shape + typography
    'rounded-lg typo-base text-space outline-none',
    // transition
    'transition-[background-color,box-shadow,border] duration-150',
    // ring + focus / open states
    'ring-0 ring-indigo-crush focus:bg-white focus:ring-2 data-[state=open]:bg-white data-[state=open]:ring-2',
    // placeholder
    'data-placeholder:text-space-60',
    // disabled
    'disabled:cursor-not-allowed disabled:opacity-40',
    // icons
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ),
  {
    variants: {
      variant: {
        default: cn(
          // background — hover is applied via group-hover on the wrapper
          // so that hovering a linked <label> does not trigger the hover style
          'bg-space-04 group-hover:not-focus:not-disabled:data-[state=closed]:bg-space-08',
        ),
        white: cn(
          // background + border
          'border border-space-40 bg-white',
          // hover — via group-hover (same label fix as default variant)
          'group-hover:not-focus:not-disabled:data-[state=closed]:bg-space-04',
          // focus / open: drop border so ring is the only affordance
          'focus:border-transparent data-[state=open]:border-transparent',
        ),
      },
      error: {
        true: 'border-transparent bg-white ring-2 ring-errors',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      error: false,
    },
  },
);

// Rendered as a sibling of SelectPrimitive.Trigger — never inside it —
// to avoid the invalid button-inside-button nesting.
const SelectClearButton = ({
  disabled,
  onClick,
  className,
}: {
  disabled: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
}) => (
  <button
    type="button"
    aria-label="Clear selection"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'flex size-8 items-center justify-center rounded-lg',
      'text-space-60 transition-colors hover:not-disabled:text-space disabled:cursor-not-allowed',
      'focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:outline-none',
      className,
    )}>
    <Icon icon={Close} className="size-4" />
  </button>
);

export const SelectTrigger = ({
  className,
  variant,
  children,
  disabled: disabledProp,
  error: errorProp,
  icon,
  showClearBtn,
  onClear,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> &
  VariantProps<typeof selectTriggerVariants> & {
    /**
     * Icon to display on the left side of the select trigger.
     */
    icon?: SvgIcon;
    /**
     * Whether the trigger is in an error state.
     * Reads from Field context when omitted.
     */
    error?: boolean;
    /**
     * Enables the clear (×) button. When true, the button appears automatically
     * when a value is selected and clears it on click via the Select context.
     */
    showClearBtn?: boolean;
    /**
     * Called when the clear button is clicked.
     * When a Select context is available, the selection is cleared automatically after this callback.
     * Use this for side-effects like analytics without needing to handle the clear logic yourself.
     */
    onClear?: React.MouseEventHandler<HTMLButtonElement>;
  }) => {
  const ctx = useSelectContextOptional();
  const fieldContext = useFieldContext();
  const error = errorProp ?? fieldContext?.error ?? false;
  const disabled = disabledProp ?? fieldContext?.disabled;

  const shouldShowClear = showClearBtn && ctx ? !!ctx.value : false;

  const handleClear: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    onClear?.(e);
    ctx?.clearValue();
  };

  return (
    // className goes on the wrapper so width constraints apply to the whole component
    // (trigger + chevron + clear button), keeping all icons within bounds.
    // This extra wrapper with `group` is needed so that hover styles use group-hover
    // instead of direct hover — prevents a linked <label> from triggering the hover state.
    <div className={cn('group relative w-full', className)}>
      {icon && (
        <Icon
          icon={icon}
          className="pointer-events-none absolute top-1/2 left-4 z-10 size-[16px] -translate-y-1/2 text-space"
        />
      )}
      <SelectPrimitive.Trigger
        data-slot="select-trigger"
        disabled={disabled}
        className={cn(
          selectTriggerVariants({ variant, error }),
          icon && 'pl-10',
          shouldShowClear ? 'pr-15' : 'pr-9',
        )}
        {...props}>
        {children}
      </SelectPrimitive.Trigger>

      {shouldShowClear && (
        <SelectClearButton
          disabled={disabled ?? false}
          onClick={handleClear}
          className="absolute top-1/2 right-8 -translate-y-1/2"
        />
      )}

      {/* Chevron — pinned to the right, never moves */}
      <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-space-60">
        <Icon icon={ChevronDown} className="size-4" />
      </span>
    </div>
  );
};

export const SelectContent = ({
  className,
  children,
  ...props
}: Omit<ComponentProps<typeof SelectPrimitive.Content>, 'position'>) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      data-slot="select-content"
      className={cn(
        'aura-select',
        // layout + sizing
        // z-[10001] — must exceed aura modals overlay/container z-[10000] so dropdowns render above the modal
        'relative z-[10001] max-h-(--radix-select-content-available-height) min-w-[var(--radix-select-trigger-width)] overflow-hidden',
        // shape + surface
        'rounded-lg border border-space-08 bg-white shadow-lg',
        // animation origin
        'origin-(--radix-select-content-transform-origin) duration-100',
        // slide direction by side
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        // open animation
        'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        className,
      )}
      sideOffset={8}
      position="popper"
      {...props}>
      <SelectPrimitive.Viewport className={cn('w-full min-w-(--radix-select-trigger-width) p-2')}>
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);

export const SelectLabel = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) => (
  <SelectPrimitive.Label
    data-slot="select-label"
    className={cn('px-4 py-2 typo-xs text-space-60', className)}
    {...props}
  />
);

export const SelectItem = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) => (
  <SelectPrimitive.Item
    data-slot="select-item"
    className={cn(
      // layout + sizing
      'relative flex w-full cursor-default items-center rounded-lg px-4 py-3 pr-10 select-none',
      // typography
      'typo-base text-space outline-none',
      // hover / focus
      'focus:bg-space-04',
      // selected state
      'data-[state=checked]:bg-indigo-crush-10',
      // selected + highlighted (keyboard) — one tone darker
      'data-highlighted:data-[state=checked]:bg-indigo-crush-20',
      // disabled
      'data-disabled:pointer-events-none data-disabled:opacity-40',
      className,
    )}
    {...props}>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <span className="pointer-events-none absolute right-4 flex size-4 items-center justify-center text-indigo-crush">
      <SelectPrimitive.ItemIndicator>
        <Icon icon={Check} className="pointer-events-none size-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
);

export const SelectSeparator = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) => (
  <SelectPrimitive.Separator
    data-slot="select-separator"
    className={cn('pointer-events-none -mx-1 my-1 h-px bg-space-08', className)}
    {...props}
  />
);
